import { ReactNode, useEffect, useState, createContext } from "react";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { ContentType } from "@dimensional-engineering/dimensional-models";

import NotificationViewModal from "@/components/appHome/NotificationViewModal/NotificationViewModal";
import RewardsModal from "@/components/appHome/rewards/RewardsModal";
import ConversionModal from "@/components/premium/ConversionModal/ConversionModal";
import GeneralModal from "@/components/shared/GeneralModal/GeneralModal";
import { useModalContext } from "@/context/ModalContext";
import { RootState } from "@/redux/store";
import { Mixpanel } from "@/helpers/mixpanel";
import PeerFeedbackModal from "@/components/appHome/PeerFeedBackModule/PeerFeedbackModal/PeerFeedbackModal";
import {
  ExtendedClientNotificationData,
  ExtendedClientNotificationType,
} from "@/models/sharedModels";
import FeatureCarouselModal from "@/components/FeatureCarouselModal/FeatureCarouselModal";
import { getListOfTraitRulesForMixpanel } from "@/components/appHome/DailyInsights/utils";
import { NotificationLoadingModal } from "@/components/shared/LoadingModal/LoadingModal";
import ProfileModalHandler from "@/components/authenticatedProfile/Abstracts/ProfileModal/ProfileModalHandler";
import { useAuth } from "./AuthContext";
import InniModal from "@/components/appHome/InniBlock/InniModal/InniModal";
import InniSoulmateModal from "@/components/appHome/InniBlock/InniSoulmateModal/InniSoulmateModal";
import WeeklyTwoByTwoModal from "@/components/appHome/WeeklyTwoByTwo/WeeklyTwoByTwoModal";
import WeeklyTierListModal from "@/components/appHome/WeeklyTierList/WeeklyTierListModal";
import WeeklyVennModal from "@/components/appHome/WeeklyVenn/WeeklyVennModal";
import { requestScrollToInniPreviewView } from "@/components/appHome/InniBlock/scrollToInniPreview";

interface Props {
  children?: ReactNode;
}

export const NotificationContext = createContext<{}>({});

export const NotificationClickHandlersProvider = ({
  children,
}: Props): JSX.Element => {
  const router = useRouter();
  const [notificationData, setNotificationData] = useState<any | null>(null);

  const { removeModalByName, setModalComponent, modalComponent } =
    useModalContext();

  const { user } = useAuth();

  const userState = useSelector((state: RootState) => state.user);
  const connectionState = useSelector((state: RootState) => state.connections);
  const dailyInsightState = useSelector(
    (state: RootState) => state.dailyInsight
  );
  const userPollState = useSelector((state: RootState) => state.userPoll);

  const openInNewTab = (url: string) => {
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  };

  function clearNotificationData() {
    localStorage.removeItem("notificationType");
    setNotificationData(null);
  }

  useEffect(() => {
    const checkLocalStorage = () => {
      const LSNotificationType = localStorage.getItem("notificationType");
      if (LSNotificationType) {
        const data = JSON.parse(LSNotificationType);
        if (notificationData === null) {
          setNotificationData(data);
        }
        localStorage.removeItem("notificationType");
      }
    };

    const intervalId = setInterval(checkLocalStorage, 1000);

    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (notificationData) {
      const {
        notificationType,
        snippetSlug,
        peerUid,
        genericModalTitle,
        genericModalBody,
        linkUrl,
        compatibilitySnippetSlug,
        reportSlug,
        participantUids,
        commentId,
        contentSlug,
        contentType,
        peerStorySlug,
        assessmentType,
        assessmentSlug,
        weeklyTwoByTwoSlug,
        weeklyTierListSlug,
        weeklyVennSlug,
      } = notificationData as ExtendedClientNotificationData;

      switch (notificationType as ExtendedClientNotificationType) {
        case "viewSnippet":
          if (snippetSlug) {
            localStorage.setItem("snippetSlug", snippetSlug);
          }
          removeModalByName("LoadingModal");
          clearNotificationData();
          router.push("/app/results");
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          break;

        case "viewProfile":
          if (peerUid && peerUid !== "null") {
            setModalComponent(
              <ProfileModalHandler uid={peerUid} elementName="profileModal" />
            );
          }
          clearNotificationData();
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          break;

        case "viewGenericModal":
          if (genericModalTitle && genericModalBody) {
            setModalComponent(
              <GeneralModal title={genericModalTitle} body={genericModalBody} />
            );
            clearNotificationData();
          }
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          break;

        case "viewPremiumConversionModal":
          setModalComponent(<ConversionModal />);
          clearNotificationData();
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          break;

        case "viewCompatibilitySnippet":
          if (peerUid && compatibilitySnippetSlug) {
            const profile = connectionState.publicProfiles?.find(
              (p) => p.ownerUUID === peerUid
            );
            if (profile) {
              localStorage.setItem(
                "copatibilitySnippetSlug",
                compatibilitySnippetSlug
              );
              setModalComponent(
                <ProfileModalHandler uid={peerUid} elementName="profileModal" />
              );
              clearNotificationData();
            }
          }
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          break;

        case "viewRewards":
          router.push("/app/home");
          setModalComponent(<RewardsModal />);
          clearNotificationData();
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          break;

        case "viewReport":
          if (reportSlug) {
            router.push(`/app/results`);

            if (userState.memberProfile?.shareHash) {
              openInNewTab(
                `https://www.dimensional.me/reports/${reportSlug}/${userState.memberProfile?.shareHash}`
              );
            }

            clearNotificationData();
          }
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          break;

        case "viewFeed":
          router.push(`/app/home`);
          setModalComponent(<NotificationViewModal />);
          clearNotificationData();
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          break;

        case "surveyLinkReceived":
          if (linkUrl) {
            openInNewTab(linkUrl);
          }
          clearNotificationData();
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          break;

        case "viewPeerAssessment":
          if (peerUid) {
            router.push(`/app/peer-assessment/peer-core/${peerUid}`);
            return clearNotificationData();
          }
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          break;

        case "viewPeerFeedbackLevelsModal":
          router.push("/app/home");
          setModalComponent(<PeerFeedbackModal />);
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          return clearNotificationData();

        case "chatMessage":
          if (participantUids) {
            const participantUidsParsed = JSON.parse(
              participantUids
            ) as string[];

            const connectionUid = participantUidsParsed?.find(
              (uid) => uid !== userState.publicProfileData?.ownerUUID
            );

            console.log("participantUidsParsed: ", participantUidsParsed);

            if (connectionUid) {
              router.push(`/app/messaging/${connectionUid}`);
            } else {
              router.push("/app/messaging");
            }
          } else {
            router.push("/app/messaging");
          }
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          return clearNotificationData();

        case "weeklyPublicProfileViews":
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          setModalComponent(<NotificationViewModal />);
          return clearNotificationData();

        case "viewPeriodicQuiz":
          router.push("/app/home");
          setModalComponent(
            <NotificationLoadingModal
              elementName="NotificationLoadingModal"
              notificationType="viewPeriodicQuiz"
            />
          );
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          return clearNotificationData();

        case "periodicQuizResultShared":
          router.push("/app/home");
          setModalComponent(
            <NotificationLoadingModal
              elementName="NotificationLoadingModal"
              notificationType="viewPeriodicQuiz"
            />
          );
          Mixpanel?.track(
            "Notification Opened",
            {
              type: "connection quiz results shared",
            },
            user?.uid
          );
          return clearNotificationData();

        case "viewFeatureCarousel":
          router.push("/app/home");
          setModalComponent(<FeatureCarouselModal />);
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          return clearNotificationData();

        case "viewDashboardInniCTA":
          router.push("/app/home");
          requestScrollToInniPreviewView();
          Mixpanel?.track(
            "Notification Opened",
            {
              type: notificationType,
            },
            user?.uid
          );
          return clearNotificationData();

        case "viewDeliveredDailyInsight":
          router.push("/app/home");
          setModalComponent(
            <NotificationLoadingModal
              elementName="NotificationLoadingModal"
              notificationType="viewDeliveredDailyInsight"
            />
          );

          Mixpanel?.track(
            "Notification Opened",
            {
              type: "viewDeliveredDailyInsight",
              body: dailyInsightState.deliveredDailyInsight?.assignmentRuleset?.body.slice(
                0,
                250
              ),
              trait_rules: getListOfTraitRulesForMixpanel(
                dailyInsightState.deliveredDailyInsight
              ),
              is_novel_ruleset:
                dailyInsightState.deliveredDailyInsight?.assignmentRuleset ??
                false,
            },
            user?.uid
          );
          return clearNotificationData();

        case "viewDeliveredDailyStoryNew":
          router.push("/app/home");
          setModalComponent(
            <NotificationLoadingModal
              elementName="NotificationLoadingModal"
              notificationType="viewDeliveredDailyStoryNew"
            />
          );
          Mixpanel?.track(
            "Notification Opened",
            {
              type: "viewDeliveredDailyStoryNew",
            },
            user?.uid
          );
          return clearNotificationData();

        case "viewUserPoll":
          router.push("/app/home");
          setModalComponent(
            <NotificationLoadingModal
              elementName="NotificationLoadingModal"
              notificationType="viewUserPoll"
            />
          );
          Mixpanel?.track(
            "Notification Opened",
            {
              type: "viewUserPoll",
              content_type: "daily poll",
              slug: userPollState.userPoll?.slug,
            },
            user?.uid
          );
          return clearNotificationData();

        case "commentLiked":
          if (contentSlug && commentId && contentType) {
            Mixpanel?.track(
              "Comment modal summoned",
              {
                source:
                  contentType === ContentType.deliveredDailyInsight
                    ? "daily insight"
                    : contentType === ContentType.deliveredDailyStory
                    ? "daily story"
                    : contentType === ContentType.periodicQuiz
                    ? "daily quiz"
                    : "daily poll",
                type: "from notification or feed",
              },
              user?.uid
            );

            Mixpanel?.track(
              "Notification Opened",
              {
                type: "commentLiked",
                comment_type: "root comment",
                content_type:
                  contentType === ContentType.deliveredDailyInsight
                    ? "daily insight"
                    : contentType === ContentType.deliveredDailyStory
                    ? "daily story"
                    : contentType === ContentType.periodicQuiz
                    ? "daily quiz"
                    : "daily poll",
              },
              user?.uid
            );
            if (contentType === ContentType.deliveredDailyInsight) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewDeliveredDailyInsight"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                />
              );
              return clearNotificationData();
            } else if (contentType === ContentType.deliveredDailyStory) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewDeliveredDailyStoryNew"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                />
              );
              return clearNotificationData();
            } else if (contentType === ContentType.periodicQuiz) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="periodicQuizResultShared"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                />
              );
              return clearNotificationData();
            } else if (contentType === ContentType.userPoll) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewUserPoll"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                />
              );
              return clearNotificationData();
            } else if (contentType === ContentType.communityQuestion) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewCommunityQuestion"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                  contentSlug={contentSlug}
                />
              );
              return clearNotificationData();
            } else {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewSharedPost"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                  contentSlug={contentSlug}
                />
              );
              return clearNotificationData();
            }
          }

        case "commentReplied":
          if (contentSlug && commentId && contentType) {
            Mixpanel?.track(
              "Comment modal summoned",
              {
                source:
                  contentType === ContentType.deliveredDailyInsight
                    ? "daily insight"
                    : contentType === ContentType.deliveredDailyStory
                    ? "daily story"
                    : contentType === ContentType.periodicQuiz
                    ? "daily quiz"
                    : "daily poll",
                type: "from notification or feed",
              },
              user?.uid
            );

            Mixpanel?.track(
              "Notification Opened",
              {
                type: "commentReplied",
                comment_type: "root comment",
                content_type:
                  contentType === ContentType.deliveredDailyInsight
                    ? "daily insight"
                    : contentType === ContentType.deliveredDailyStory
                    ? "daily story"
                    : contentType === ContentType.periodicQuiz
                    ? "daily quiz"
                    : "daily poll",
              },
              user?.uid
            );
            if (contentType === ContentType.deliveredDailyInsight) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewDeliveredDailyInsight"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                />
              );
              return clearNotificationData();
            } else if (contentType === ContentType.deliveredDailyStory) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewDeliveredDailyStoryNew"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                />
              );
              return clearNotificationData();
            } else if (contentType === ContentType.periodicQuiz) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="periodicQuizResultShared"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                />
              );
              return clearNotificationData();
            } else if (contentType === ContentType.userPoll) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewUserPoll"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                />
              );
              return clearNotificationData();
            } else if (contentType === ContentType.communityQuestion) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewCommunityQuestion"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                  contentSlug={contentSlug}
                />
              );
              return clearNotificationData();
            } else {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewSharedPost"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                  contentSlug={contentSlug}
                />
              );
              return clearNotificationData();
            }
          }

        case "firstComment":
          if (commentId && contentType) {
            Mixpanel?.track(
              "Comment modal summoned",
              {
                source:
                  contentType === ContentType.deliveredDailyInsight
                    ? "daily insight"
                    : contentType === ContentType.deliveredDailyStory
                    ? "daily story"
                    : contentType === ContentType.periodicQuiz
                    ? "daily quiz"
                    : "daily poll",
                type: "from notification or feed",
              },
              user?.uid
            );

            Mixpanel?.track(
              "Notification Opened",
              {
                type: "firstComment",
                comment_type: "root comment",
                content_type:
                  contentType === ContentType.deliveredDailyInsight
                    ? "daily insight"
                    : contentType === ContentType.deliveredDailyStory
                    ? "daily story"
                    : contentType === ContentType.periodicQuiz
                    ? "daily quiz"
                    : "daily poll",
              },
              user?.uid
            );
            if (contentType === ContentType.deliveredDailyInsight) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewDeliveredDailyInsight"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                />
              );
              return clearNotificationData();
            } else if (contentType === ContentType.deliveredDailyStory) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewDeliveredDailyStoryNew"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                />
              );
              return clearNotificationData();
            } else if (contentType === ContentType.periodicQuiz) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="periodicQuizResultShared"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                />
              );
              return clearNotificationData();
            } else if (contentType === ContentType.userPoll) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewUserPoll"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                />
              );
              return clearNotificationData();
            } else if (contentType === ContentType.communityQuestion) {
              setModalComponent(
                <NotificationLoadingModal
                  commentContent="viewCommunityQuestion"
                  commentId={commentId}
                  notificationType="comment"
                  elementName="NotificationLoadingModal"
                  contentSlug={contentSlug}
                />
              );
              return clearNotificationData();
            }
          }

        case "viewSharedPost":
          router.push("/app/home");
          setModalComponent(
            <NotificationLoadingModal
              elementName="NotificationLoadingModal"
              notificationType="viewSharedPost"
              contentSlug={contentSlug}
            />
          );
          return clearNotificationData();

        case "postLiked":
          router.push("/app/home");
          setModalComponent(
            <NotificationLoadingModal
              elementName="NotificationLoadingModal"
              notificationType="viewSharedPost"
              contentSlug={contentSlug}
            />
          );
          return clearNotificationData();

        case "connectionPostCreated":
          router.push("/app/home");
          setModalComponent(
            <NotificationLoadingModal
              elementName="NotificationLoadingModal"
              notificationType="viewSharedPost"
              contentSlug={contentSlug}
            />
          );
          return clearNotificationData();

        case "postCommented":
          Mixpanel?.track(
            "Comment modal summoned",
            {
              type: "from notification or feed",
            },
            user?.uid
          );

          Mixpanel?.track(
            "Notification Opened",
            {
              type: "postCommented",
            },
            user?.uid
          );
          if (contentSlug && commentId) {
            setModalComponent(
              <NotificationLoadingModal
                commentContent="viewSharedPost"
                commentId={commentId}
                notificationType="comment"
                elementName="NotificationLoadingModal"
                contentSlug={contentSlug}
              />
            );
            return clearNotificationData();
          }

        case "viewCommunityQuestion":
          if (contentSlug) {
            Mixpanel?.track(
              "Notification Opened",
              {
                type: "dailyCommunityQuestionReceived",
                content_type: "daily community question",
                slug: contentSlug,
              },
              user?.uid
            );
            router.push(`/app/home`);
            setModalComponent(
              <NotificationLoadingModal
                notificationType="viewCommunityQuestion"
                elementName="NotificationLoadingModal"
                contentSlug={contentSlug}
              />
            );
            return clearNotificationData();
          }

        case "viewScheduledAssessment":
          if (assessmentSlug && assessmentType) {
            Mixpanel?.track(
              "Notification Opened",
              {
                type: "viewScheduledAssessment",
                slug: assessmentSlug,
              },
              user?.uid
            );
            localStorage.setItem(
              "viewScheduledAssessment",
              JSON.stringify({
                assessmentSlug: assessmentSlug,
                assessmentType: assessmentType,
              })
            );
          }

        case "introducingINNI":
          setModalComponent(<InniModal />);
          return clearNotificationData();
        
        case "viewINNIAfterSignupModal":
          setModalComponent(<InniSoulmateModal />);
          return clearNotificationData();

        case "viewWeeklyTwoByTwo":
          router.push("/app/home");
          setModalComponent(
            <WeeklyTwoByTwoModal initialSelectedUserUid={peerUid ?? undefined} />
          );
          Mixpanel?.track(
            "Notification Opened",
            {
              type: "viewWeeklyTwoByTwo",
              slug: weeklyTwoByTwoSlug ?? contentSlug,
            },
            user?.uid
          );
          return clearNotificationData();

        case "viewWeeklyTierList":
          router.push("/app/home");
          setModalComponent(
            <WeeklyTierListModal initialSelectedUserUid={peerUid ?? undefined} />
          );
          Mixpanel?.track(
            "Notification Opened",
            {
              type: "viewWeeklyTierList",
              slug: weeklyTierListSlug ?? contentSlug,
            },
            user?.uid
          );
          return clearNotificationData();

        case "viewWeeklyVenn":
          router.push("/app/home");
          setModalComponent(
            <WeeklyVennModal initialSelectedUserUid={peerUid ?? undefined} />
          );
          Mixpanel?.track(
            "Notification Opened",
            {
              type: "viewWeeklyVenn",
              slug: weeklyVennSlug ?? contentSlug,
            },
            user?.uid
          );
          return clearNotificationData();

        default:
          console.warn(`Unhandled notification type: ${notificationType}`);
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationData]);

  return (
    <NotificationContext.Provider value={{}}>
      {children}
    </NotificationContext.Provider>
  );
};
